import React, { FC, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import {
  Button,
  Informer,
  CountDown,
  CodeInput,
  CodeInputRef,
  washRegex,
  Svg,
  Countdown,
} from "ii-ui-kit";

import { ReactComponent as ArrowLeft } from "icons/chevron-left.svg";
import { formatPhoneNumber } from "utils/formatters";
import "./sms-block.scss";

const noop = () => undefined;

export type SMSBlockProps = {
  /** Длинна отправленного кода */
  codeLength: number;
  onSubmit: (code: string) => void;
  /** Колбек на нажатие "Отправить код повторно" */
  onResend?: () => void;
  /** Объект countdown. reference: src/components/countdown/use-countdown.ts:22 */
  countdown: Countdown;
  className?: string;
};

export const SMSBlock: FC<SMSBlockProps> = ({
  codeLength,
  onSubmit,
  onResend,
  countdown,
  className,
}) => {
  const [isBackButtonLoading, setIsBackButtonLoading] = useState(false);
  // Время действия кода истекло ?
  const isCodeExpired = window.error === "smsAuthCodeExpired";
  // Превышено число попыток(без блокировки) ?
  const isMaxAttemptsReached = window.error === "smsAuthCodeLimit";

  const [attemptsLeft, setAttemptLeft] = useState(3);

  useEffect(() => {
    // при маунте компонента, в случае, если в window.error, есть ошибка keycloak-а
    // устанавливаем компонент в состояние ошибки и очищаем форму
    if (window.error) {
      const match = window.error.match(/smsAuthCodeInvalid(\d+)/);
      if (match !== null && match[1]) {
        setAttemptLeft(Number(match[1]));
        setState({
          valid: false,
          disabled: false,
          value: "",
        });
      }

      if (isMaxAttemptsReached) {
        setState({
          valid: true,
          disabled: true,
          value: "",
        });
      }
    }
  }, []);

  const codeInputRef = useRef<CodeInputRef>({ focus: noop });

  const [state, setState] = useState({
    value: "",
    valid: true,
    disabled: false,
  });
  const [isOpenInformer, setIsOpenInformer] = useState(false);

  const handleResend = () => {
    onResend?.();
    setState({ value: "", valid: true, disabled: false });
    codeInputRef.current.focus();
  };

  const onCodeChange = (code: string) => {
    setState({ value: code, valid: true, disabled: false });
    const numbers = code.replace(washRegex.denySpace.number, "");
    if (numbers.length !== codeLength) {
      return;
    }

    onSubmit(code);
  };

  const handleCloseInformer = () => setIsOpenInformer(false);

  const handleOpenInformer = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsOpenInformer(true);
  };

  const formatedPhoneNumber = () => {
    if (!window.phoneNumber) {
      return "";
    }
    return formatPhoneNumber(`7${window.phoneNumber}`);
  };

  const renderDescription = () => {
    if (isCodeExpired || isMaxAttemptsReached) {
      return (
        <div className={"sms-block__code-expired"}>
          {isCodeExpired
            ? "Время действия кода истекло"
            : "Превышено число попыток"}
        </div>
      );
    }

    return (
      <div className="sms-block__text">
        Код подтверждения для входа отправлен на телефонный номер{" "}
        {formatedPhoneNumber()}
      </div>
    );
  };

  const handleBackButton = () => {
    if (!window.loginRestartFlowUrl) {
      return;
    }

    setIsBackButtonLoading(true);
    window.location.replace(window.loginRestartFlowUrl);
  };

  return (
    <div
      className={classNames("sms-block", className)}
      onClick={handleCloseInformer}
    >
      <div className="sms-block__title">Код из SMS</div>
      {renderDescription()}
      <CodeInput
        length={codeLength}
        value={state.value}
        onChange={onCodeChange}
        disabled={state.disabled}
        valid={state.valid}
        autoFocus={!state.disabled}
        className="sms-block__input"
        controlRef={codeInputRef}
        attemptValidationCount={attemptsLeft}
      />
      <div className={"sms-block__countdown"}>
        <CountDown
          countdown={countdown}
          timedOutText="Отправить код повторно"
          renderRunningText={(timeLeft) =>
            `Отправить код повторно можно через ${timeLeft}.`
          }
          onTimedOutClick={handleResend}
        />
      </div>
      <div className="sms-block__no-getting-sms">
        <div
          className="sms-block__no-getting-sms-btn"
          onClick={handleOpenInformer}
        >
          <span>Не приходит SMS</span>
          <Svg name={isOpenInformer ? "info-painted" : "info"} />
        </div>

        <Informer
          className="sms-block__informer"
          isOpen={isOpenInformer}
          onClose={handleCloseInformer}
          text={
            <div>
              <div>1. Проверьте корректность введенного номера телефона.</div>
              <div>
                2. Убедитесь, что у телефона выключены «Авиарежим» или режим «Не
                беспокоить».
              </div>
              <div>
                3. Возможно, вы изменили номер телефона и не предоставили его
                нам. Для оперативного решения вопроса свяжитесь с нами: 8 (800)
                100-48-98.
              </div>
              <div>
                4. Проблема может быть на стороне вашего сотового оператора.
                Обратитесь в их службу поддержки.
              </div>
            </div>
          }
        />
      </div>
      <Button
        isLoading={isBackButtonLoading}
        className={"sms-block__back-button"}
        color={"secondary"}
        title={"Назад"}
        size={"m"}
        onClick={handleBackButton}
        renderLeftContent={() => {
          return <ArrowLeft />;
        }}
      />
    </div>
  );
};
